<template>
	<div class="block-editor-container subflow-block-editor">
		<StepDetailsHeader
			v-on="$listeners"
			hideActionText
			:icon="subflowServiceSlug"
			:hideProfileSelectionOption="!isProfileSelectionNeeded"
			:isContextualNode="false"
			:showDropdownMenu="false"
		/>

		<div class="editor-body flex-stretch-to-scroll pa-4 font-size-md">
			<div class="font-weight-600">Choose subflow</div>
			<SubflowSelect
				:projectId="projectId"
				:currentSubflowId="parseInt(currentSubflowId)"
				@onSubflowSelected="(v) => setSelectedSubflow(String(v))"
			/>
			<div class="horizontal-divider mt-4 mb-4" v-if="publishedSubflowInput || draftSubflowInput" />
			<SubFlowParamsForm
				class="mt-6"
				:fieldsDefinitions="selectedSubflow?.is_published ? publishedSubflowInput : draftSubflowInput"
				:node="editNode"
				@updateSubflowParams="updateSubflowParams"
			/>
			<Loader v-if="isLoadingSubflow" />

			<SubflowSampleDataModal
				v-if="showSampleDataModal"
				:node="sampleDataNode"
				:isSendingSampleData="isSendingSampleData"
				:fieldsDefinitions="draftSubflowInput"
				@sendTestDataToSubflow="sendTestDataToSubflow"
				@setIsWaitingForSample="setIsWaitingForSample"
				@updateSubflowParams="(v) => (sampleData = v)"
			/>
		</div>
		<div v-if="!showSampleDataModal && testResponse">
			<BlockEditorBottomBar
				btnTextInput="Test"
				:isTestingWidget="false"
				:testResultStatus="testStatus"
				:showTestResultModal.sync="isShowTestResultModal"
				:testResponse="testResponse"
				:showParams="false"
				:isCloudFlow="false"
				:hasEditPermissions="hasEditPermissions"
				:btnFuncLoading="isBtnFuncLoading"
				@btnFunc="testSubflow"
			>
				<template v-slot:content v-if="isTestStatusUnKnown">
					<div class="trigger-mode-bottom-bar flex-centerize flex-column">
						<div v-if="!selectedSubflow?.is_published" class="soft-message font-size-sm mt-2 mb-2">
							<div>You cannot test an unpublished subflow.</div>
							<div>Publish the subflow to test this step</div>
						</div>
						<SecondaryButton large :disabled="isSendingSampleData || btnDisabled" @click="testSubflow">Test</SecondaryButton>
					</div>
				</template>
			</BlockEditorBottomBar>
		</div>
		<div class="test-modal narrow-widget-max-width" :class="{ 'is-visible': isShowTestResultModal }">
			<TestResultModal :isError="false" @onClose="isShowTestResultModal = false">
				<TestResultsDataProvider
					:showTestResultModal.sync="isShowTestResultModal"
					:testBtnLoading="isTriggerSetupLoading"
					:testResponseTruncate="testResponseTruncate"
					@test-again="applyTestAgain"
				/>
			</TestResultModal>
			<!-- <div v-if="loadingTestAndContinue">
				<Loader />
			</div> -->
		</div>
		<div class="editor-overlay" :class="{ show: isShowTestResultModal }" @click="isShowTestResultModal = false">
			<!--	<Loader v-if="loadingTestAndContinue" /> -->
		</div>
	</div>
</template>

<script>
import api from '@/api';
import { PLY_PARAMS } from '@/utils/ParamsService';
import { prepareInputData } from '@ply-io/node-input-parser';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { getWorkflowData, setWaitingForSampleData } from '@/store/modules/workflows/actions';
import { getVersionOfWorkflow } from '@/store/modules/workflows/modules/versions/actions';
import { getWorkflow, getWorkflowCurrentVersion } from '@/store/modules/workflows/getters';
import { getNode } from '@/store/modules/nodes/getters';
import { updateNodeTestResult } from '@/store/modules/nodes/actions';
import {
	fetchSubflows,
	fetchSubflowParam,
	createWorkflowPusherConnection,
	removeWorkflowPusherConnection,
} from '@/store/modules/subflows/actions';
import { isWaitingForSampleData } from '@/store/modules/workflows/getters';
import { getSubflowParams } from '@/store/modules/subflows/getters';
import { setParamInEditNode, testStep, updateEditNodeTestResultData } from '@/store/modules/selectedNode/actions';
import { editNode, flatPreviousSteps } from '@/store/modules/selectedNode/getters';
import { WAITING_FLAG_TYPES } from '@/utils/workflow-service.const';

import BlockEditorBridge from '../block-editor-bridge';

import TestModalService from '../helpers/test-modal-service';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';

import StepDetailsHeader from '../StepDetailsHeader';
import BlockEditorBottomBar from '../BlockEditorBottomBar';
import TestResultModal from '../TestResultModal';
import TestResultsDataProvider from '../helpers/TestResultsDataProvider';
import SubflowSelect from './helpers/SubflowSelect';
import SubFlowParamsForm from './helpers/SubFlowParamsForm';
import SubflowSampleDataModal from './helpers/SubflowSampleDataModal';

import { replaceInsertDataStringDictionary } from '@/store/modules/selectedWorkflow/getters';
import { configurationsMappingBlocksUUIDs } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { TEST_RESULT_STATUSES } from '@/utils/workflow-node.const';
import { APP_CONFIRMATION_DIALOG_RESPONSES } from '@/components/dialogs/AppDialogs/app-confirmation-dialog.const.js';

import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'SubflowBlockEditor',
	components: {
		StepDetailsHeader,
		BlockEditorBottomBar,
		TestResultModal,
		TestResultsDataProvider,
		SubflowSelect,
		SubFlowParamsForm,
		SubflowSampleDataModal,
	},
	props: {
		nodeID: { type: [Number, String] },
		hasEditPermissions: { type: Boolean },
	},
	data() {
		return {
			isLoadingSubflow: false,
			isShowParams: null,
			isShowTestResultModal: false,
			isShowAnimation: false,
			isTestedDataChanged: false,
			isBtnFuncLoading: false,
			isChangeAction: false,
			isTriggerSetupLoading: false,
			hasTestParams: false,
			timer: null,
			isSendingSampleData: false,
			currentSubflowVersion: null,
			testResponse: {},
			sampleData: {},
		};
	},
	computed: {
		...mapGettersShorthand({
			editNode,
			editWorkflow,
			getWorkflow,
			getSubflowParams,
			isWaitingForSampleData,
			flatPreviousSteps,
			replaceInsertDataStringDictionary,
			configurationsMappingBlocksUUIDs,
			getWorkflowCurrentVersion,
			getNode,
		}),
		projectId() {
			return this.editWorkflow.project;
		},
		selectedSubflow() {
			return this.currentSubflowId && this.getWorkflow(this.currentSubflowId);
		},
		subflowInputId() {
			return this.selectedSubflow.current_draft.sub_flow_input_configuration;
		},
		unPublishedSubflowInputId() {
			return this.editNode.params_map;
		},
		draftSubflowInput() {
			return this.selectedSubflow && this.getSubflowParams(this.subflowInputId);
		},
		publishedSubflowOutput() {
			return (
				this.selectedSubflow &&
				this.currentSubflowVersion &&
				this.getSubflowParams(this.currentSubflowVersion?.sub_flow_output_configuration)
			);
		},
		publishedSubflowInput() {
			return this.currentSubflowVersion ? this.getSubflowParams(this.currentSubflowVersion.sub_flow_input_configuration) : null;
		},
		currentSubflowId() {
			return this.editNode.params_map.sub_flow_id;
		},
		stepHeaderIcon() {
			return BlockEditorBridge.stepHeaderIcon();
		},
		isProfileSelectionNeeded() {
			return BlockEditorBridge.isProfileSelectionNeeded();
		},
		btnDisabled() {
			return !this.selectedSubflow?.is_published;
		},
		isWaiting() {
			return this.isWaitingForSampleData(this.currentSubflowId);
		},
		showSampleDataModal() {
			return this.isWaiting;
		},
		testStatus() {
			return this.getNode(this.editNode.id).test_result_status; // edit node status is not always synced with the real node data after changes
		},
		isTestStatusUnKnown() {
			return this.testStatus === TEST_RESULT_STATUSES.UNKNOWN;
		},
		sampleDataNode() {
			return { ...this.editNode, params_map: this.sampleData };
		},
		subflowServiceSlug() {
			return ServiceSlugs.SubFlow;
		},
	},
	methods: {
		...mapActionsShorthand({
			fetchSubflows,
			getWorkflowData,
			getVersionOfWorkflow,
			fetchSubflowParam,
			setParamInEditNode,
			testStep,
			createWorkflowPusherConnection,
			removeWorkflowPusherConnection,
			setWaitingForSampleData,
			updateNodeTestResult,
			updateEditNodeTestResultData,
		}),
		async testSubflow() {
			this.testResponse = null;
			const runningNodes = this.getWorkflowCurrentVersion(this.selectedSubflow.id)?.nodes.length - 2;
			const dialogProps = {
				title: 'Test subflow?',
				description: `When you test a subflow, all steps in the subflow run. This subflow will run ${runningNodes} steps.`,
				softNote: "To send sample data to the subflow, open the subflow's trigger step and enter test mode.",
				yesText: 'Confirm',
				isDanger: false,
				contentMaxWidth: 330,
			};
			const res = await AppDialogManagerService.openConfirmationDialog(dialogProps);
			await this.fetchSubflowCurrentVersion();
			if (res === APP_CONFIRMATION_DIALOG_RESPONSES.YES) {
				const testResult = await AppDialogManagerService.openSubflowTestDialog({
					currentNodeId: this.editNode.id,
					subflowHash: this.selectedSubflow.hash,
				});

				this.testResponse = {
					test_result: testResult?.output,
					test_result_output_labels: {},
					test_result_status: testResult?.output ? TEST_RESULT_STATUSES.SUCCESS : TEST_RESULT_STATUSES.UNKNOWN,
				};
				// sync both node in nodes and edit node in selected node
				await this.updateEditNodeTestResultData({ ...this.testResponse });
				await this.updateNodeTestResult({ id: this.editNode.id, testResponse: this.testResponse });

				this.isShowTestResultModal = true;
			}
		},
		async setSelectedSubflow(id) {
			this.isLoadingSubflow = true;

			const removedSubFlowId = this.currentSubflowId;
			if (removedSubFlowId) {
				await this.removeWorkflowPusherConnection({ subFlowId: removedSubFlowId });
			}

			// set new sub flow so we look at the new sub flow id
			const paramsMapValue = { sub_flow_id: id };
			const newParamsMap = { key: 'params_map', value: paramsMapValue };
			await this.setParamInEditNode(newParamsMap);

			// connect to pusher events for the sub flow
			await this.createWorkflowPusherConnection({ subFlowId: id });

			// get all relevant data needed for this new sub flow after setting it
			await this.initSubflowData(id);
			this.isLoadingSubflow = false;
		},
		async initSubflowData(id) {
			this.isLoadingSubflow = true;
			await this.fetchSubflows({ projectId: this.projectId });
			await this.getWorkflowData({ id, getSubFlowConfigurationParams: true, getWorkflowWaitingFlag: true });
			await this.fetchSubflowCurrentVersion();
			this.isLoadingSubflow = false;
		},
		updateSubflowParams(newOutputParams) {
			this.setParamInEditNode({ key: 'params_map', value: { ...this.editNode.params_map, ...newOutputParams }, options: {} });
		},
		async sendTestDataToSubflow() {
			const paramsMap = this.sampleData;
			const interpolatedData = Object.keys(paramsMap).reduce((params, pmKey) => {
				const valueObject = { value: paramsMap[pmKey]?.[PLY_PARAMS.listSourceParam.key] || paramsMap[pmKey] };
				// prepareInputData changes the input object and does not return a value
				prepareInputData(
					[],
					valueObject,
					this.flatPreviousSteps,
					this.replaceInsertDataStringDictionary,
					this.configurationsMappingBlocksUUIDs
				);
				return {
					...params,
					[pmKey]: valueObject.value,
				};
			}, {});
			this.isSendingSampleData = true;

			await api.sendTestDataToSubflowInputs(this.currentSubflowId, interpolatedData);
		},
		async fetchSubflowCurrentVersion() {
			this.currentSubflowVersion = null;
			if (this.selectedSubflow.current_version) {
				this.currentSubflowVersion = await this.getVersionOfWorkflow({
					workflowId: this.selectedSubflow.id,
					workflowVersionId: this.selectedSubflow.current_version,
					forceGet: true,
				});
				await this.fetchSubflowParam({ id: this.currentSubflowVersion.sub_flow_input_configuration, forceGet: true });
			}
		},
		testResponseTruncate(response) {
			return TestModalService.mapTestResponseToModalView(response, this.publishedSubflowOutput);
		},
		applyTestAgain() {
			this.isShowTestResultModal = false;
			this.testSubflow();
		},
		async setIsWaitingForSample(isWaiting) {
			await this.setWaitingForSampleData({
				id: this.currentSubflowId,
				is_waiting: isWaiting,
				waiting_type: WAITING_FLAG_TYPES.SUB_FLOW_SAMPLE,
			});
		},
	},
	async mounted() {
		await this.fetchSubflows({ projectId: this.projectId });
		if (this.currentSubflowId) {
			this.initSubflowData(this.currentSubflowId);
		}
		this.sampleData = { ...this.editNode.params_map };
	},
	watch: {
		showSampleDataOption(newV, oldV) {
			if (!newV && oldV && this.isSendingSampleData) {
				const dialogProps = {
					title: 'Sample data sent',
					description: `Open subflow to see test results`,
					yesText: 'Got it',
					isDanger: false,
				};
				AppDialogManagerService.openConfirmationDialog(dialogProps);
			}
			this.isSendingSampleData = false;
		},
		showSampleDataModal(newV, oldV) {
			if (!newV && oldV && this.isSendingSampleData) {
				const dialogProps = {
					title: 'Sample data sent',
					description: `Open subflow to see test results`,
					yesText: 'Got it',
					isDanger: false,
				};
				AppDialogManagerService.openConfirmationDialog(dialogProps);
			}
			this.isSendingSampleData = false;
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/components/WidgetForm/widget-form-mixins.scss';
@import '../block-editor.scss';

.trigger-mode-bottom-bar {
	padding: 16px 8px;

	> ::v-deep(.modal-container) {
		border: 1px solid var(--seconday-border-color) !important;
		box-shadow: var(--card-shadow) !important;

		a {
			color: var(--secondary-font-color);
		}
	}

	::v-deep(.v-btn) {
		min-width: 150px !important;
	}
}
</style>
